import { useState } from 'react';
import { Play } from 'lucide-react';
import { Badge, Button, Input, Select, Alert } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import { METHODS } from '@/lib/mockApiSpec';

/**
 * "Try it" — fire a request at the SAVED spec and see which route and case answer it.
 *
 * Hits POST /api/mock-api/environments/:id/try, which persists nothing, never goes
 * upstream even in proxy/record mode, and forces the failure rate off. All three
 * matter: a Try It that silently spent a real API call, wrote a request-log row, or
 * randomly 503'd would make the one tool for debugging a mock untrustworthy.
 */
const textareaClass = 'w-full border rounded-md px-2 py-1.5 text-sm font-mono bg-[var(--app-surface)]';
const errText = (ex) => (ex.response && ex.response.data && ex.response.data.error) || ex.message;

const MATCH_LABEL = {
  case: 'matched a case',
  'route-only': 'route matched, no case did',
  none: 'no route matched',
};

export default function TryItPanel({ environmentId, dirty }) {
  const { fetchApi } = useAppApi();
  const [method, setMethod] = useState('GET');
  const [path, setPath] = useState('/');
  const [body, setBody] = useState('');
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    setErr('');
    try {
      const [bare, search = ''] = path.split('?');
      const query = Object.fromEntries(new URLSearchParams(search).entries());
      setResult(await fetchApi('mockTry', {
        id: environmentId, method, path: bare || '/', query, body: body || undefined,
      }));
    } catch (ex) {
      setErr(errText(ex));
      setResult(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      {dirty && (
        <Alert variant="warning" title="Unsaved changes">
          Try it runs against the SAVED spec. Save first to test what you are editing.
        </Alert>
      )}
      {err && <Alert variant="error" title="Could not run">{err}</Alert>}

      <div className="flex items-end gap-2">
        <div className="w-28 shrink-0">
          <Select label="Method" options={METHODS.map((m) => ({ label: m, value: m }))} value={method} onChange={setMethod} />
        </div>
        <div className="flex-1">
          <Input label="Path" value={path} onChange={(e) => setPath(e.target.value)} placeholder="/v1/charges/ch_9?expand=customer" />
        </div>
        <Button size="sm" onClick={run} disabled={busy}>
          <Play size={13} /> {busy ? 'Running…' : 'Send'}
        </Button>
      </div>

      <label className="block space-y-1">
        <span className="text-sm">Request body</span>
        <textarea className={textareaClass} rows={4} value={body} onChange={(e) => setBody(e.target.value)} placeholder='{"currency":"USD"}' />
      </label>

      {result && (
        <div className="rounded-lg border border-[var(--app-border)] overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-[var(--app-border)]">
            <Badge variant={result.status >= 400 ? 'error' : 'success'} size="sm">{result.status}</Badge>
            <span className="text-xs text-secondary-500">
              {MATCH_LABEL[(result.headers || {})['x-mock-match']] || result.outcome}
              {(result.headers || {})['x-mock-route'] ? ` — ${result.headers['x-mock-route']}` : ''}
              {(result.headers || {})['x-mock-scenario'] ? ` · scenario ${result.headers['x-mock-scenario']}` : ''}
            </span>
          </div>
          <pre className="px-3 py-2 text-xs font-mono whitespace-pre-wrap break-all max-h-72 overflow-auto">{result.body}</pre>
          {(result.warnings || []).length > 0 && (
            <div className="px-3 py-2 border-t border-[var(--app-border)] text-xs text-amber-700">
              {result.warnings.map((w, i) => <div key={i}>{w}</div>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
